// src/context/LocationContext.tsx
import { LocationService } from "@/lib/location";
import React, { createContext, useContext, useEffect, useState, useMemo } from "react";

type Location = {
  latitude: number;
  longitude: number;
  accuracy?: number;
  timestamp: number;
  speed?: number | null;
  heading?: number | null;
};

type LocationContextType = {
  location: Location | null;
  isTracking: boolean;
  startTracking: () => Promise<void>;
  stopTracking: () => void;
  refreshLocation: () => Promise<void>;
  service: LocationService;
  getCurrentLocation: () => Promise<Location>;
};

const LocationContext = createContext<LocationContextType | undefined>(undefined);

export const LocationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [location, setLocation] = useState<Location | null>(null);
  const [isTracking, setIsTracking] = useState(false);
  const service = useMemo(() => new LocationService(), []);

  useEffect(() => {
    const interval = setInterval(() => {
      setLocation(service.getLastKnownLocation());
      setIsTracking(service.isCurrentlyTracking());
    }, 5000); // sync every 5s

    return () => clearInterval(interval);
  }, [service]);

  const startTracking = async () => {
    try {
      await service.startTracking();
      setIsTracking(true);
      setLocation(service.getLastKnownLocation());
    } catch (err) {
      console.error("Failed to start location tracking:", err);
      setIsTracking(false)
    }
  };

  const stopTracking = () => {
    service.stopTracking();
    setIsTracking(false);
  };

  const refreshLocation = async () => {
    try {
      const newLocation = await service.getCurrentPosition();
      setLocation(newLocation);
    } catch (err) {
      console.error("Failed to refresh location:", err);
    }
  };

  const getCurrentLocation = async () => {
    return await service.getCurrentPosition();
  };

  return (
    <LocationContext.Provider
      value={{
        location,
        isTracking,
        startTracking,
        stopTracking,
        refreshLocation,
        service,
        getCurrentLocation
      }}
    >
      {children}
    </LocationContext.Provider>
  );
};

/** Hook for easy access */
export const useLocation = () => {
  const context = useContext(LocationContext);
  if (!context) throw new Error("useLocation must be used within a LocationProvider");
  return context;
};
